import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Refresh, DonutLarge, RotateLeft } from '@mui/icons-material';
import { Box } from '@mui/material';
import { eventBus, EventTypes } from '@core/pubsub/EventBus';
import { dashboardService } from '@/services/dashboardService';
import { ShopId } from '@core/store/slices/apiSlice';

// Apex Charts with React
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { BaseWidget } from '@/components/widgets/BaseWidget.tsx';
import { useNavigate } from 'react-router-dom';
import { TrolleyShelterData } from '@core/types';

interface TrolleySheltersCountCapacityProps {
    shopIdName: ShopId;
    heading: string;
    className?: string;
}

const TrolleySheltersCountCapacity: React.FC<TrolleySheltersCountCapacityProps> = ({
    shopIdName,
    heading,
    className
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<TrolleyShelterData[]>([]);
    const [chartHeight, setChartHeight] = useState(200);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const result = await dashboardService.loadTrolleySheltersCountCapacity(shopIdName.id);
            setData(result || []);
        } catch (error) {
            console.error('Failed to load trolley shelters count capacity:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [shopIdName.id]);

    useEffect(() => {
        const unsubscribe = eventBus.on(EventTypes.WIDGETS_RELOAD, load);
        load();

        return unsubscribe;
    }, [load]);

    const handleFullscreenChange = useCallback((fullscreen: boolean) => {
        setIsFullscreen(fullscreen);
        console.log('fullscreen:', fullscreen);
        setChartHeight(fullscreen ? window.innerHeight - 100 : 200);
    }, []);

    const handleDailyDetailsClick = () => {
        navigate(`/statistics/rfid/dailydetails?shopId=${shopIdName.id}`);
    };

    const menuActions = [
        {
            text: t('refresh'),
            icon: <Refresh fontSize="small" />,
            onClick: load
        },
        {
            text: t('dailyDetails'),
            icon: <DonutLarge fontSize="small" />,
            onClick: () => {
                handleDailyDetailsClick();
            }
        }
    ];

    // Prepare chart data
    const series: ApexAxisChartSeries = [
        {
            name: t('currentCount'),
            data: data.map((item) => ({
                x: [item.moduleId, `${t('capacity')}: ${item.capacity}`],
                y: item.currentCount ?? 0
            }))
        },
        {
            name: t('diffToCapacity'),
            data: data.map((item) => ({
                x: item.moduleId,
                y: item.capacity - (item.currentCount ?? 0)
            }))
        }
    ];

    const maxCapacity = Math.max(...data.map((item) => item.capacity), 0);

    const options: ApexOptions = {
        chart: {
            type: 'bar',
            height: chartHeight,
            stacked: true,
            toolbar: {
                show: false
            },
            background: 'transparent',
            fontFamily: 'Wanzl_Etelka Light, Roboto, sans-serif'
        },
        plotOptions: {
            bar: {
                horizontal: true
            }
        },
        colors: ['#8AC926', '#D9534F'],
        grid: {
            show: false
        },
        xaxis: {
            labels: {
                show: true
            },
            type: 'category'
        },
        yaxis: {
            labels: {
                show: isFullscreen
            },
            max: maxCapacity
        },
        tooltip: {
            x: {
                show: true
            }
        },
        legend: {
            show: false
        },
        dataLabels: {
            enabled: false
        },
        theme: {
            mode: 'light'
        },
        noData: {
            text: t('noData'),
            style: {
                fontSize: '18px'
            }
        }
    };

    return (
        <BaseWidget
            heading={heading}
            icon={<RotateLeft sx={{ transform: 'rotate(180deg)' }} />}
            className={className}
            customActions={menuActions}
            loading={loading}
            onFullscreen={handleFullscreenChange}
            isFullscreen={isFullscreen}
        >
            <Box sx={{ px: 1, paddingLeft: '4px', paddingRight: '4px' }}>
                <Chart
                    key={`chart-${isFullscreen}-${chartHeight}`}
                    options={options}
                    series={series}
                    type="bar"
                    height={chartHeight}
                />
            </Box>
        </BaseWidget>
    );
};

export default TrolleySheltersCountCapacity;
